import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {useAppDispatch, useAppSelector} from '../../../redux/hooks'
import {showErrorMessage, showSuccessMessage} from '_metronic/helpers/swalHelper'
import i18n from 'i18n'
import clsx from 'clsx'
import CustomSelect from 'app/custom/CustomSelect'
import {Relations, RelationsFr} from '_metronic/helpers/jsonData'
import {storeEmergencyContact} from '../../../redux/Slice/studentSlice'

const EmergencyContact = ({modelType}) => {
  const {t} = useTranslation()
  const [loading, setLoading] = useState(false)
  const dispatch = useAppDispatch()
  const {errors} = useAppSelector((state) => state.tools)

  const validationSchema = Yup.object().shape({
    first_name: Yup.string().required(t('VALIDATION.REQUIRED_', {name: t('LABEL.FIRST_NAME')})),
    last_name: Yup.string().required(t('VALIDATION.REQUIRED_', {name: t('LABEL.LAST_NAME')})),
    contact_number: Yup.string().required(
      t('VALIDATION.REQUIRED_', {name: t('LABEL.CONTACT_NUMBER')})
    ),
    email: Yup.string().required(t('VALIDATION.REQUIRED_', {name: t('PROFILE.EMAIL')})),
    address: Yup.string().required(t('VALIDATION.REQUIRED_', {name: t('LABEL.ADDRESS')})),
    relationship: Yup.string().required(t('VALIDATION.SELECT_', {name: t('LABEL.RELATIONSHIP')})),
  })

  const formik = useFormik({
    initialValues: {
      first_name: '',
      last_name: '',
      contact_number: '',
      email: '',
      address: '',
      relationship: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values, {setSubmitting, resetForm}) => {
      setLoading(true)
      const formData = new FormData()
      formData.append('first_name', values.first_name)
      formData.append('last_name', values.last_name)
      formData.append('contact_number', values.contact_number)
      formData.append('email', values.email)
      formData.append('address', values.address)
      formData.append('relationship', values.relationship)
      formData.append('model_type', modelType)

      dispatch(storeEmergencyContact(formData)).then((res) => {
        if (res.meta.requestStatus === 'fulfilled') {
          showSuccessMessage(t('MESSAGES.CREATED'), () => {})
          window.location.reload()
        } else if (res.meta.requestStatus === 'rejected') {
          showErrorMessage(t('MESSAGES.SERVER_ERROR'))
        }
        setLoading(false)
        setSubmitting(false)
      })
    },
  })

  return (
    <>
      <div
        className='modal show'
        style={{display: 'block', backgroundColor: 'rgba(0,0,0,0.5)'}}
        tabIndex={-1}
      >
        <div className='modal-dialog modal-lg'>
          <div className='modal-content'>
            <div className='modal-header p-3 bg-primary'>
              <h5 className='modal-title text-white'>{t('LABEL.EMERGENCY_CONTACT')}</h5>
            </div>
            <form
              className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
              noValidate
              autoComplete='off'
              onSubmit={formik.handleSubmit}
            >
              <div className='modal-body'>
                <p className='fw-bold text-justify' style={{textAlign: 'justify'}}>
                  {i18n.language === 'en' ? (
                    <>
                      Please provide the full contact details (full name, relationship, telephone
                      number, and email address) of the person to be contacted in the event of a
                      medical emergency or exceptional situation, should the need arise. This
                      information will be treated as strictly confidential and used solely for
                      safety and duty-of-care purposes.
                    </>
                  ) : (
                    <>
                      Veuillez renseigner les coordonnées complètes (nom et prénom, lien de parenté,
                      numéro de téléphone, e-mail) de la personne à contacter en cas d’urgence
                      médicale ou de situation exceptionnelle, en cas de besoin. Ces informations
                      sont strictement confidentielles et utilisées exclusivement à des fins de
                      sécurité et de prise en charge.
                    </>
                  )}
                </p>
                <hr />

                <div className='row'>
                  <div className='col-6'>
                    <label className='col-form-label label fw-bolder required'>
                      {t('LABEL.FIRST_NAME')}
                    </label>
                    <input
                      type='text'
                      placeholder={t('LABEL.FIRST_NAME')}
                      {...formik.getFieldProps('first_name')}
                      className={clsx(
                        'form-control form-control-sm text-capitalize',
                        {
                          'is-invalid':
                            (formik.touched.first_name && formik.errors.first_name) ||
                            errors.first_name,
                        },
                        {
                          'is-valid': formik.touched.first_name && !formik.errors.first_name,
                        }
                      )}
                    />
                    {formik.touched.first_name && formik.errors.first_name && (
                      <span className='text-danger fw-bold'>{formik.errors.first_name}</span>
                    )}
                  </div>
                  <div className='col-6'>
                    <label className='col-form-label label fw-bolder required'>
                      {t('LABEL.LAST_NAME')}
                    </label>
                    <input
                      type='text'
                      placeholder={t('LABEL.LAST_NAME')}
                      {...formik.getFieldProps('last_name')}
                      className={clsx(
                        'form-control form-control-sm text-uppercase',
                        {
                          'is-invalid':
                            (formik.touched.last_name && formik.errors.last_name) ||
                            errors.last_name,
                        },
                        {
                          'is-valid': formik.touched.last_name && !formik.errors.last_name,
                        }
                      )}
                    />
                    {formik.touched.last_name && formik.errors.last_name && (
                      <span className='text-danger fw-bold'>{formik.errors.last_name}</span>
                    )}
                  </div>
                  <div className='col-6'>
                    <label className='col-form-label label fw-bolder required'>
                      {t('LABEL.CONTACT_NUMBER')}
                    </label>
                    <input
                      type='text'
                      placeholder={t('LABEL.CONTACT_NUMBER')}
                      {...formik.getFieldProps('contact_number')}
                      className={clsx(
                        'form-control form-control-sm',
                        {
                          'is-invalid':
                            (formik.touched.contact_number && formik.errors.contact_number) ||
                            errors.contact_number,
                        },
                        {
                          'is-valid':
                            formik.touched.contact_number && !formik.errors.contact_number,
                        }
                      )}
                    />
                    {formik.touched.contact_number && formik.errors.contact_number && (
                      <span className='text-danger fw-bold'>{formik.errors.contact_number}</span>
                    )}
                  </div>
                  <div className='col-6'>
                    <label className='col-form-label label fw-bolder required'>
                      {t('PROFILE.EMAIL')}
                    </label>
                    <input
                      type='email'
                      placeholder={t('PROFILE.EMAIL')}
                      {...formik.getFieldProps('email')}
                      className={clsx(
                        'form-control form-control-sm',
                        {
                          'is-invalid':
                            (formik.touched.email && formik.errors.email) || errors.email,
                        },
                        {
                          'is-valid': formik.touched.email && !formik.errors.email,
                        }
                      )}
                    />
                    {formik.touched.email && formik.errors.email && (
                      <span className='text-danger fw-bold'>{formik.errors.email}</span>
                    )}
                  </div>
                  <div className='col-12'>
                    <label className='col-form-label label fw-bolder required'>
                      {t('LABEL.RELATIONSHIP')}
                    </label>
                    <CustomSelect
                      name='relationship'
                      className='input'
                      onChange={(rel: any) => formik.setFieldValue('relationship', rel.id)}
                      value={formik.values.relationship}
                      options={i18n.language === 'en' ? Relations : RelationsFr}
                      placeholderText={t('LABEL.RELATIONSHIP')}
                    />
                    {formik.touched.relationship && formik.errors.relationship && (
                      <span className='text-danger fw-bold'>{formik.errors.relationship}</span>
                    )}
                  </div>
                  <div className='col-12'>
                    <label className='col-form-label label fw-bolder required'>
                      {t('LABEL.ADDRESS')}
                    </label>
                    <input
                      type='text'
                      placeholder={t('LABEL.ADDRESS')}
                      {...formik.getFieldProps('address')}
                      className={clsx(
                        'form-control form-control-sm',
                        {
                          'is-invalid':
                            (formik.touched.address && formik.errors.address) || errors.address,
                        },
                        {
                          'is-valid': formik.touched.address && !formik.errors.address,
                        }
                      )}
                    />
                    {formik.touched.address && formik.errors.address && (
                      <span className='text-danger fw-bold'>{formik.errors.address}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className='modal-footer'>
                <div className='fs-6 fw-bold text-gray-700'>
                  <button
                    type='submit'
                    className='btn btn-sm btn-light btn-active-primary fw-bolder'
                    disabled={formik.isSubmitting || loading}
                  >
                    {!loading && (
                      <span className='indicator-label'>
                        <i className='fas fa-save mx-2'></i>
                        {t('BUTTONS.SAVE')}
                      </span>
                    )}
                    {loading && (
                      <span className='indicator-progress' style={{display: 'block'}}>
                        {t('GLOBAL.LOADING')}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    )}
                  </button>
                  <div />
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  )
}

export default EmergencyContact
